/**
 * Utilitaire centralisé pour le téléchargement de PDF.
 *
 * Stratégie robuste compatible iframe Emergent preview / popup blockers / sandbox :
 *  - GET  : navigation directe de la fenêtre courante vers l'URL backend (qui renvoie
 *           Content-Disposition: attachment). Le navigateur déclenche le téléchargement
 *           sans changer de page. Fonctionne dans tout iframe sandbox, sans popup-blocker.
 *  - POST : fetch + blob → on assigne le blob URL à `window.location` (même technique).
 *
 * Feedback utilisateur via `sonner` (toast).
 */
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const buildUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Déclenche un téléchargement en chargeant l'URL dans un iframe caché.
 * - Pour une URL backend (GET) : le serveur envoie `Content-Disposition: attachment`
 *   → le navigateur déclenche le download et ne change pas de page.
 * - Pour un blob URL (POST) : idem, le navigateur respecte le type MIME application/pdf.
 *
 * Fonctionne dans tout contexte (Chrome direct, iframe sandbox Emergent), sans popup-blocker.
 * Note : pour les blob URL, le navigateur peut utiliser un nom de fichier auto (UUID).
 * Pour avoir un nom propre, on essaie en complément `<a download>` (no-op dans certains sandboxes).
 */
const triggerDownload = (href, filename, isBlob = false) => {
  // Pour les blob URLs, complément `<a download>` pour fournir le nom de fichier (ignoré silencieusement
  // dans certains iframes sandbox, mais utile partout ailleurs).
  if (isBlob && filename) {
    try {
      const link = document.createElement('a');
      link.href = href;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return; // si <a download> a fonctionné, pas besoin de l'iframe
    } catch { /* ignore */ }
  }

  // Mécanisme principal : iframe caché. Le navigateur respecte Content-Disposition: attachment.
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = href;
    document.body.appendChild(iframe);
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch { /* ignore */ }
    }, 30_000);
  } catch (e) {
    console.error('[downloadPdf] triggerDownload iframe failed:', e);
    throw e;
  }
};

/**
 * Téléchargement PDF unifié.
 */
export async function downloadPdf({
  url,
  filename,
  method = 'GET',
  body = null,
  includeAuth = true,
  silent = false,
  successMessage,
  errorMessage,
} = {}) {
  if (!url) throw new Error('downloadPdf: url manquante');

  const fullUrl = buildUrl(url);

  // === GET : déclenchement immédiat via iframe caché + window.open + lien cliquable de secours ===
  if (method === 'GET') {
    try {
      // Tentative 1 : iframe caché + <a download> (fonctionne hors sandbox)
      triggerDownload(fullUrl, filename);
      // Tentative 2 : ouverture dans nouvel onglet (fonctionne dans la plupart des sandboxes avec allow-popups)
      try { window.open(fullUrl, '_blank', 'noopener,noreferrer'); } catch { /* ignore */ }

      // Filet de sécurité ultime : lien manuel cliquable dans le toast.
      // Un clic utilisateur direct contourne TOUS les sandbox / popup-blockers.
      if (!silent) {
        toast.success(successMessage || 'Téléchargement du PDF…', {
          description: 'Si le téléchargement ne démarre pas, cliquez sur le lien ci-dessous :',
          duration: 12_000,
          action: {
            label: 'Télécharger',
            onClick: () => {
              const a = document.createElement('a');
              a.href = fullUrl;
              a.target = '_blank';
              a.rel = 'noopener noreferrer';
              if (filename) a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            },
          },
        });
      }
      return;
    } catch (error) {
      console.error('Erreur ouverture PDF (GET):', error);
      if (!silent) toast.error(errorMessage || `Erreur lors du téléchargement du PDF : ${error.message}`);
      throw error;
    }
  }

  // === POST : fetch + blob + déclenchement via iframe + lien de secours ===
  const headers = { 'Content-Type': 'application/json' };
  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: body !== null ? JSON.stringify(body) : null,
    });
    if (!response.ok) {
      let detail = '';
      try {
        const text = await response.text();
        try { detail = JSON.parse(text).detail || text; } catch { detail = text; }
      } catch { /* ignore */ }
      throw new Error(`HTTP ${response.status}${detail ? ' — ' + String(detail).slice(0, 200) : ''}`);
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    triggerDownload(objectUrl, filename, true);
    try { window.open(objectUrl, '_blank', 'noopener,noreferrer'); } catch { /* ignore */ }

    if (!silent) {
      toast.success(successMessage || 'PDF généré', {
        description: 'Si le téléchargement ne démarre pas, cliquez sur le lien ci-dessous :',
        duration: 12_000,
        action: {
          label: 'Télécharger',
          onClick: () => {
            const a = document.createElement('a');
            a.href = objectUrl;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            if (filename) a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          },
        },
      });
    }

    // Révocation différée pour laisser à l'utilisateur le temps de cliquer sur l'action du toast.
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 120_000);
  } catch (error) {
    console.error('Erreur téléchargement PDF (POST):', error);
    if (!silent) toast.error(errorMessage || `Erreur lors du téléchargement du PDF : ${error.message}`);
    throw error;
  }
}

export default downloadPdf;
